import { CellRef, Rule, RuleType, TableTotalsSpec } from "../../components/interfaces/Rule";
import { extractDataFromXml, getDataFromCustomXml, persistDataToCustomXml } from "./CustomXmlHelper";

export const CUSTOM_XML_RULE_NAMESPACE = "CustomXMLCoRule";

const KNOWN_RULE_TYPES = new Set<string>(Object.values(RuleType));
const toRuleType = (raw: unknown): RuleType =>
  typeof raw === "string" && KNOWN_RULE_TYPES.has(raw) ? (raw as RuleType) : RuleType.CUSTOM;

const sanitizeTableTotals = (tt: any): TableTotalsSpec | undefined => {
  if (typeof tt !== "object" || tt === null) return undefined;
  const isCell = (c: any): boolean => !!c && typeof c.row === "number" && typeof c.column === "number";
  const normalizeCell = (c: any): CellRef => ({
    row: c.row,
    column: c.column,
    id: typeof c.id === "string" ? c.id : undefined,
  });
  const addends: CellRef[] = Array.isArray(tt.addends) ? tt.addends.filter(isCell).map(normalizeCell) : [];
  if (addends.length < 1 || !isCell(tt.subtotal)) return undefined;
  return {
    tableId: typeof tt.tableId === "string" ? tt.tableId : "",
    addends,
    subtotal: normalizeCell(tt.subtotal),
  };
};

export const parseRuleXmlToRules = (xml: string): Rule[] => {
  try {
    if (!xml) return [];
    const jsonText = extractDataFromXml(xml);
    if (!jsonText) {
      console.warn("[CustomXmlCoRuleHelper][parseRuleXmlToRules] CDATA section not found or empty.");
      return [];
    }
    const parsed = JSON.parse(jsonText);
    if (!Array.isArray(parsed)) {
      console.warn("[CustomXmlCoRuleHelper][parseRuleXmlToRules] JSON in CDATA is not an array.");
      return [];
    }
    return parsed
      .filter(
        (r: any) =>
          r &&
          typeof r.id === "string" &&
          typeof r.title === "string" &&
          typeof r.version === "number" &&
          typeof r.script === "string"
      )
      .map((r: any) => ({
        id: r.id as string,
        type: toRuleType(r.type),
        title: r.title as string,
        version: r.version as number,
        script: r.script as string,
        tagIds: Array.isArray(r.tagIds) ? (r.tagIds as string[]) : undefined,
        tableTotals: sanitizeTableTotals(r.tableTotals),
      }));
  } catch (e) {
    console.error("[CustomXmlCoRuleHelper][parseRuleXmlToRules] Error parsing XML/JSON:", e);
    return [];
  }
};

export const getRulesFromCustomXml = async (): Promise<Rule[]> => {
  const logPrefix = "[CustomXmlCoRuleHelper][getRulesFromCustomXml]";
  return getDataFromCustomXml(CUSTOM_XML_RULE_NAMESPACE, parseRuleXmlToRules, logPrefix);
};

export const persistRulesToCustomXml = async (rules: Rule[]): Promise<void> => {
  const logPrefix = "[CustomXmlCoRuleHelper][persistRulesToCustomXml]";
  await persistDataToCustomXml(CUSTOM_XML_RULE_NAMESPACE, rules, undefined, logPrefix);
};

export const addRuleToCustomXml = async (rule: Rule): Promise<void> => {
  const logPrefix = "[CustomXmlCoRuleHelper][addRuleToCustomXml]";
  try {
    console.debug(`${logPrefix} Adding rule:`, rule);
    const existingRules = await getRulesFromCustomXml();
    const updatedRules = [...existingRules, rule];
    await persistRulesToCustomXml(updatedRules);
    console.debug(`${logPrefix} Rule added successfully`);
  } catch (e) {
    console.error(`${logPrefix} Error adding rule:`, e);
    throw e;
  }
};

export const editRuleInCustomXml = async (rule: Rule): Promise<void> => {
  const logPrefix = "[CustomXmlCoRuleHelper][editRuleInCustomXml]";
  try {
    console.debug(`${logPrefix} Editing rule:`, rule);
    const existingRules = await getRulesFromCustomXml();
    const updatedRules = existingRules.map((r) => (r.id === rule.id ? rule : r));
    await persistRulesToCustomXml(updatedRules);
    console.debug(`${logPrefix} Rule edited successfully`);
  } catch (e) {
    console.error(`${logPrefix} Error editing rule:`, e);
    throw e;
  }
};

export const deleteRuleFromCustomXml = async (ruleId: string): Promise<Rule[]> => {
  const logPrefix = "[CustomXmlCoRuleHelper][deleteRuleFromCustomXml]";
  try {
    console.log(`${logPrefix} Deleting rule with ID: ${ruleId}`);
    const currentRules = await getRulesFromCustomXml();
    const updatedRules = currentRules.filter((r) => r.id !== ruleId);
    await persistRulesToCustomXml(updatedRules);
    console.log(`${logPrefix} Rule deleted successfully`);
    return updatedRules;
  } catch (e) {
    console.error(`${logPrefix} Error deleting rule:`, e);
    throw e;
  }
};
